<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,

      // Zimobi
      inactiveConversationTimeoutMinutes: 15,
      inactiveConversationTimeoutValue: 15,
      inactiveConversationTimeoutUnit: 'Minutes',
      inactiveConversationAction: 'open',
      // Zimobi
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
      accountId: 'getCurrentAccountId',
    }),
    currentAccount() {
      return this.$store.getters['accounts/getAccount'](this.accountId);
    },
    isExecuteInactiveConversationsActionsFeatureEnabled() {
      return !!this.currentAccount?.feat_execute_inactive_conversations_actions;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;

      // Zimobi
      const minutes =
        this.activeAgentBot.inactive_conversation_timeout_minutes ?? 15;
      this.inactiveConversationTimeoutMinutes = minutes;
      const { value, unit } = this.minutesToDisplayUnit(minutes);
      this.inactiveConversationTimeoutValue = value;
      this.inactiveConversationTimeoutUnit = unit;
      this.inactiveConversationAction =
        this.activeAgentBot.inactive_conversation_action ?? 'open';
      // Zimobi
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    // Zimobi
    async updateAgentBotSettings() {
      const minutes = this.displayUnitToMinutes(
        this.inactiveConversationTimeoutValue,
        this.inactiveConversationTimeoutUnit
      );
      if (minutes < 15) {
        useAlert(
          this.$t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.MIN_VALUE_ERROR')
        );
        return;
      }
      if (minutes > 4320) {
        useAlert(
          this.$t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.MAX_VALUE_ERROR')
        );
        return;
      }
      try {
        await this.$store.dispatch('agentBots/update', {
          id: this.activeAgentBot.id,
          data: {
            inactive_conversation_timeout_minutes: minutes,
            inactive_conversation_action: this.inactiveConversationAction,
          },
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    displayUnitToMinutes(value, unit) {
      const multipliers = { Minutes: 1, Hours: 60, Days: 1440 };
      return Math.round(value * (multipliers[unit] || 1));
    },
    minutesToDisplayUnit(minutes) {
      if (minutes % 1440 === 0) {
        return { value: minutes / 1440, unit: 'Days' };
      }
      if (minutes % 60 === 0) {
        return { value: minutes / 60, unit: 'Hours' };
      }
      return { value: minutes, unit: 'Minutes' };
    },
    // Zimobi
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="flex flex-wrap mx-0"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsSection
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <div class="w-3/5">
          <label>
            <select v-model="selectedAgentBotId" data-testid="agent-bot-select">
              <option value="" disabled selected>
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="agentBot in agentBots"
                :key="agentBot.id"
                :value="agentBot.id"
              >
                {{ agentBot.name }}
              </option>
            </select>
          </label>
          <div class="button-container">
            <NextButton
              type="submit"
              data-testid="update-agent-bot"
              :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
              :is-loading="uiFlags.isSettingAgentBot"
            />
            <NextButton
              type="button"
              faded
              ruby
              data-testid="disconnect-bot"
              :disabled="!selectedAgentBotId"
              :is-loading="uiFlags.isDisconnecting"
              :label="$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT')"
              class="button--disconnect"
              @click="disconnectBot"
            />
          </div>
        </div>
      </SettingsSection>
    </form>
    <!-- Zimobi -->
    <SettingsSection
      v-if="
        activeAgentBot &&
        activeAgentBot.id &&
        isExecuteInactiveConversationsActionsFeatureEnabled
      "
      :title="$t('AGENT_BOTS.INACTIVE_CONVERSATION_SETTINGS.TITLE')"
      :sub-title="$t('AGENT_BOTS.INACTIVE_CONVERSATION_SETTINGS.DESC')"
    >
      <div class="w-3/5">
        <label>
          {{ $t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.LABEL') }}
        </label>
        <div class="flex items-center gap-3 mb-4">
          <label class="flex-grow mb-0">
            <input
              v-model.number="inactiveConversationTimeoutValue"
              type="number"
              min="1"
              data-testid="inactive-timeout-value"
              :placeholder="
                $t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.PLACEHOLDER')
              "
            />
          </label>
          <select
            v-model="inactiveConversationTimeoutUnit"
            data-testid="inactive-timeout-unit"
            class="min-w-[8rem]"
          >
            <option value="Minutes">
              {{ $t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.UNITS.MINUTES') }}
            </option>
            <option value="Hours">
              {{ $t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.UNITS.HOURS') }}
            </option>
            <option value="Days">
              {{ $t('AGENT_BOTS.INACTIVE_CONVERSATION_TIMEOUT.UNITS.DAYS') }}
            </option>
          </select>
        </div>
        <label>
          {{ $t('AGENT_BOTS.INACTIVE_CONVERSATION_ACTION.LABEL') }}
          <select
            v-model="inactiveConversationAction"
            data-testid="inactive-action"
          >
            <option value="open">
              {{ $t('AGENT_BOTS.INACTIVE_CONVERSATION_ACTION.OPTIONS.OPEN') }}
            </option>
            <option value="resolve">
              {{
                $t('AGENT_BOTS.INACTIVE_CONVERSATION_ACTION.OPTIONS.RESOLVE')
              }}
            </option>
          </select>
        </label>
        <NextButton
          type="button"
          data-testid="update-inactive-settings"
          :label="$t('AGENT_BOTS.INACTIVE_CONVERSATION_SETTINGS.SUBMIT')"
          :is-loading="uiFlags.isUpdating"
          @click="updateAgentBotSettings"
        />
      </div>
    </SettingsSection>
    <!-- Zimobi -->
  </div>
</template>

<style scoped lang="scss">
.button--disconnect {
  @apply ml-2;
}
</style>
